import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { IconBookmark, IconBookmarkFill, IconHouse, IconHouseFill, IconPersonCircle, IconPersonFill, IconSearch, Logo } from "@/components/icons/Icons";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";
import { LOW_POWER } from "@/lib/perf";
import { haptic } from "@/lib/haptics";
import { useCompactTitle } from "@/components/common/LargeTitle";
import { useSettings } from "@/context/SettingsContext";
import { AnimatePresence } from "framer-motion";

const LINKS = [
  { to: "/", label: "Bosh sahifa", end: true, id: "home" },
  { to: "/movies", label: "Filmlar", id: "movies" },
  { to: "/series", label: "Seriallar", id: "series" },
  { to: "/categories", label: "Kategoriyalar", id: "categories" },
  { to: "/watchlist", label: "Watchlist", id: "watchlist" },
];

export function Avatar({ user, size = 32 }) {
  if (user?.picture) return <img src={user.picture} alt="" referrerPolicy="no-referrer" className="rounded-full object-cover ring-1 ring-white/15" style={{ width: size, height: size }} />;
  return (
    <span className="grid place-items-center rounded-full bg-white/[.12] font-semibold text-white ring-1 ring-white/15" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {initials(user?.name || "?")}
    </span>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const loc = useLocation();
  const compact = useCompactTitle();
  const { settings } = useSettings();
  const brand = settings?.site?.name || "UzPrime";
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <>
      {/* Desktop floating glass nav */}
      <header className="fixed inset-x-0 top-4 z-50 hidden justify-center px-4 lg:flex" data-testid="top-nav">
        <nav aria-label="Asosiy" className={cn("flex h-[58px] w-[min(1180px,calc(100vw-32px))] items-center gap-1 rounded-full px-2.5 transition-[background-color,box-shadow] duration-300", scrolled ? "pk-glass" : "pk-glass bg-white/[.04]")}>
          <Link to="/" className="mr-3 rounded-full px-2 py-1" aria-label={`${brand} — bosh sahifa`} data-testid="top-nav-logo">
            <Logo name={brand} />
          </Link>
          <div className="flex flex-1 items-center gap-0.5">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} data-testid={`top-nav-${l.id}-link`} className="relative rounded-full px-3.5 py-2 text-[14px] font-semibold">
                {({ isActive }) => (
                  <>
                    {isActive && <motion.span layoutId="nav-pill" className="absolute inset-0 rounded-full bg-white/[.12] ring-1 ring-white/10" transition={{ type: "spring", stiffness: 480, damping: 38 }} />}
                    <span className={cn("relative transition-colors", isActive ? "text-white" : "text-white/60 hover:text-white")}>{l.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
          <Link to="/search" data-testid="top-nav-search-button" className={cn("flex h-10 w-[230px] items-center gap-2 rounded-full border border-white/10 bg-white/[.06] px-3.5 text-[14px] font-medium text-white/50 transition-colors hover:bg-white/[.1] hover:text-white/80", loc.pathname === "/search" && "bg-white/[.12] text-white/80")}>
            <IconSearch size={17} /> Kinoni izlash...
          </Link>
          <Link to="/profile" className="ml-1.5 grid h-10 w-10 place-items-center rounded-full text-white/80 hover:bg-white/[.08] hover:text-white" aria-label="Profil" data-testid="top-nav-profile-link">
            {user ? <Avatar user={user} size={32} /> : <IconPersonCircle size={24} />}
          </Link>
        </nav>
      </header>

      {/* Mobile compact top bar */}
      <header className={cn("fixed inset-x-0 top-0 z-50 flex items-center justify-between px-4 pb-2 transition-[background-color,backdrop-filter,border-color] duration-300 lg:hidden", scrolled || compact.visible ? (LOW_POWER ? "border-b border-white/[.06] bg-[#0B0B0D]/95" : "border-b border-white/[.06] bg-[#070708]/70 backdrop-blur-xl") : "border-b border-transparent bg-transparent")} style={{ paddingTop: "calc(10px + var(--safe-top))" }} data-testid="mobile-top-bar">
        <Link to="/" aria-label={`${brand} — bosh sahifa`} className={cn("transition-opacity duration-300", compact.visible && "opacity-0")}>
          <Logo name={brand} />
        </Link>
        <AnimatePresence>
          {compact.visible && compact.title && (
            <motion.span
              key={compact.title}
              initial={{ opacity: 0, y: 8, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 420, damping: 34 }}
              className="pointer-events-none absolute inset-x-16 truncate text-center text-[17px] font-extrabold tracking-[-0.01em] text-white"
              style={{ top: "calc(18px + var(--safe-top))" }}
              data-testid="compact-title"
            >
              {compact.title}
            </motion.span>
          )}
        </AnimatePresence>
        <Link to="/search" aria-label="Qidiruv" className="pk-glass grid h-10 w-10 place-items-center rounded-full text-white" data-testid="mobile-search-button">
          <IconSearch size={19} />
        </Link>
      </header>
    </>
  );
}

const TABS = [
  { to: "/", label: "Asosiy", icon: IconHouse, active: IconHouseFill, id: "home", end: true },
  { to: "/search", label: "Qidiruv", icon: IconSearch, active: IconSearch, id: "search" },
  { to: "/watchlist", label: "Watchlist", icon: IconBookmark, active: IconBookmarkFill, id: "watchlist" },
  { to: "/profile", label: "Profil", icon: IconPersonCircle, active: IconPersonFill, id: "profile" },
];

export function MobileDock() {
  return (
    <nav aria-label="Pastki menyu" className="fixed inset-x-0 z-50 flex justify-center px-3 lg:hidden" style={{ bottom: "max(12px, var(--safe-bottom))" }} data-testid="bottom-dock">
      <div className="pk-glass pk-blur-keep grid h-[66px] w-[min(420px,calc(100vw-24px))] grid-cols-4 items-center rounded-full bg-[rgba(24,24,28,.55)] px-1.5">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} onClick={() => haptic(6)} data-testid={`bottom-dock-${t.id}`} className="relative flex h-[54px] flex-col items-center justify-center gap-0.5 rounded-full" aria-label={t.label}>
            {({ isActive }) => {
              const Icon = isActive ? t.active : t.icon;
              return (
                <>
                  {isActive && <motion.span layoutId="dock-pill" className="absolute inset-0 rounded-full bg-white/[.14] shadow-[inset_0_1px_0_rgba(255,255,255,.18)] ring-1 ring-white/[.10]" transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.9 }} />}
                  <motion.span animate={{ scale: isActive ? 1.08 : 1, opacity: isActive ? 1 : 0.6 }} whileTap={{ scale: 0.82 }} transition={{ type: "spring", stiffness: 500, damping: 26 }} className="relative text-white">
                    <Icon size={22} />
                  </motion.span>
                  <span className={cn("relative text-[10.5px] font-semibold", isActive ? "text-white" : "text-white/55")}>{t.label}</span>
                </>
              );
            }}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
